import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  InputNumber,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import { Link, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectors } from 'state';
import {
  AdvanceInput,
  loadPending,
  savePending,
  seconds,
  TandemError,
  TandemPair,
  tandemRequest,
  TandemStep,
} from './api';

export default function TandemControl() {
  const { pairId } = useParams<{ pairId: string }>();
  const username = useSelector(selectors.username);
  const storage = `tandem-advance:${username}:${pairId}`;
  const base = `/tandem-comparisons/${pairId}/`;
  const [pair, setPair] = useState<TandemPair | null>(null);
  const [pending, setPending] = useState<AdvanceInput | null>(() =>
    loadPending(storage),
  );
  const [error, setError] = useState('');
  const [stale, setStale] = useState(true);
  const [busy, setBusy] = useState('');
  const [autoBusy, setAutoBusy] = useState(false);
  const [interval, setIntervalSeconds] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  const serverClock = useRef({ date: Date.now(), received: performance.now() });
  // Completion is terminal for this pair; another route must still load.
  const complete = pair?.id === pairId && pair.state === 'complete';
  const accept = useCallback((value: TandemPair) => {
    setPair(previous => {
      // An overlapping poll must not undo a more recent mutation response.
      if (
        previous &&
        previous.id === value.id &&
        (previous.revision > value.revision ||
          previous.auto_settings_revision > value.auto_settings_revision ||
          Date.parse(previous.server_time) > Date.parse(value.server_time))
      )
        return previous;
      serverClock.current = {
        date: Date.parse(value.server_time),
        received: performance.now(),
      };
      return value;
    });
    setStale(false);
  }, []);
  useEffect(() => {
    if (complete) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    const controller = new AbortController();
    const poll = async () => {
      try {
        const value = await tandemRequest<TandemPair>(
          base,
          'GET',
          undefined,
          controller.signal,
        );
        if (!stopped) {
          accept(value);
          if (value.state === 'complete') return;
        }
      } catch (reason) {
        if (!stopped) {
          setStale(true);
          setError(String(reason));
        }
      }
      if (!stopped) timer = setTimeout(() => void poll(), 1000);
    };
    void poll();
    return () => {
      stopped = true;
      controller.abort();
      clearTimeout(timer);
    };
  }, [base, accept, complete]);
  useEffect(() => {
    if (complete) return;
    const timer = setInterval(
      () =>
        setNow(
          serverClock.current.date +
            performance.now() -
            serverClock.current.received,
        ),
      100,
    );
    return () => clearInterval(timer);
  }, [complete]);
  useEffect(() => {
    if (pending && pair?.steps.some(step => step.key === pending.key)) {
      savePending(storage, null);
      setPending(null);
    }
  }, [pair, pending, storage]);
  const advance = async () => {
    if (!pair || busy) return;
    const input = pending || {
      key: crypto.randomUUID(),
      expected_pair_revision: pair.revision,
    };
    savePending(storage, input);
    setPending(input);
    setBusy('advance');
    setError('');
    try {
      accept(await tandemRequest<TandemPair>(`${base}advance/`, 'POST', input));
      savePending(storage, null);
      setPending(null);
    } catch (reason) {
      setError(String(reason));
      if (
        reason instanceof TandemError &&
        reason.status >= 400 &&
        reason.status < 500
      ) {
        savePending(storage, null);
        setPending(null);
      }
    } finally {
      setBusy('');
    }
  };
  const auto = async (enabled: boolean) => {
    if (!pair || autoBusy) return;
    setAutoBusy(true);
    setError('');
    try {
      accept(
        await tandemRequest<TandemPair>(`${base}auto-advance/`, 'PUT', {
          enabled,
          interval_seconds: enabled
            ? (interval ?? pair.interval_seconds)
            : pair.interval_seconds,
          expected_auto_settings_revision: pair.auto_settings_revision,
        }),
      );
      setIntervalSeconds(null);
    } catch (reason) {
      setError(String(reason));
    } finally {
      setAutoBusy(false);
    }
  };
  const resume = async () => {
    if (!pair || busy) return;
    const retryStorage = `tandem-continue:${username}:${pairId}`;
    const input = loadPending<AdvanceInput>(retryStorage) || {
      key: crypto.randomUUID(),
      expected_pair_revision: pair.revision,
    };
    savePending(retryStorage, input);
    setBusy('continue');
    setError('');
    try {
      accept(
        await tandemRequest<TandemPair>(`${base}continue/`, 'POST', input),
      );
      savePending(retryStorage, null);
    } catch (reason) {
      setError(String(reason));
      if (reason instanceof TandemError && reason.status < 500)
        savePending(retryStorage, null);
    } finally {
      setBusy('');
    }
  };
  if (!pair) return error ? <Alert type="error" message={error} /> : <Spin />;
  const step = pair.current_step || pair.steps[0];
  const elapsed = step
    ? Math.max(0, (now - Date.parse(step.triggered_at)) / 1000)
    : 0;
  const duration = (side: 'legacy' | 'fast') =>
    !step
      ? 'No advances yet'
      : step[`${side}_seconds`] !== null
        ? seconds(step[`${side}_seconds`])
        : `${elapsed.toFixed(1)} s · awaiting confirmation`;
  const countdown = pair.next_due_at
    ? Math.max(0, (Date.parse(pair.next_due_at) - now) / 1000)
    : null;
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Link to="/dashboard/sessions">Back to sessions</Link>
      <Typography.Title level={3}>
        Tandem comparison <Tag>{pair.id.slice(0, 8)}</Tag>
      </Typography.Title>
      <Typography.Text>
        {pair.blueprint.name} · v{pair.blueprint.version} ·{' '}
        {pair.blueprint.difficulty} · {pair.trainer.username} /{' '}
        {pair.klass.name}
      </Typography.Text>
      <Alert
        type={
          pair.state === 'inspection'
            ? 'error'
            : pair.state === 'complete'
              ? 'success'
              : 'info'
        }
        message={
          pair.state === 'complete'
            ? 'Comparison complete — fast session ended. No extra legacy closing tick will run.'
            : pair.advance_reason ||
              'Both engines ready. Trading is open in each session.'
        }
      />
      {error && (
        <Alert
          type="error"
          message={error}
          closable
          onClose={() => setError('')}
        />
      )}
      {stale && (
        <Alert
          type="warning"
          message="Connection unconfirmed. New advances are disabled until the server responds."
        />
      )}
      <Space wrap>
        <Button
          type="primary"
          loading={busy === 'advance'}
          disabled={!!busy || stale || (!pending && !pair.advance_available)}
          onClick={() => void advance()}
        >
          {pending ? 'Check same advance request' : 'Advance both'}
        </Button>
        <InputNumber<number>
          aria-label="Auto advance interval in seconds"
          min={1}
          max={86400}
          precision={0}
          value={interval ?? pair.interval_seconds}
          disabled={!!busy}
          onChange={setIntervalSeconds}
        />
        <Typography.Text>seconds after both engines finish</Typography.Text>
        <Button
          loading={autoBusy}
          disabled={
            autoBusy ||
            (!pair.auto_enabled && (stale || !pair.start_auto_available))
          }
          onClick={() => void auto(!pair.auto_enabled)}
        >
          {pair.auto_enabled ? 'Pause auto advance' : 'Start auto advance'}
        </Button>
        {pair.auto_enabled && interval !== null && (
          <Button disabled={autoBusy || stale} onClick={() => void auto(true)}>
            Apply interval
          </Button>
        )}
        {pair.continue_available && (
          <Button
            disabled={!!busy || stale}
            loading={busy === 'continue'}
            onClick={() => void resume()}
          >
            Continue recorded work
          </Button>
        )}
      </Space>
      <Typography.Text type="secondary">
        Pausing auto advance leaves both sessions open for trading. An accepted
        advance always waits for both engines; manual advances are available
        between steps, even with auto advance on.
      </Typography.Text>
      <Typography.Text>
        {pair.auto_enabled
          ? countdown === null
            ? 'Auto advance on · waiting for both engines'
            : `Next automatic advance in ${countdown.toFixed(1)} s`
          : 'Auto advance off'}
      </Typography.Text>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: 16,
        }}
      >
        {pair.sessions.map(room => (
          <Card
            key={room.id}
            title={`${room.side === 'legacy' ? 'Legacy' : 'Fast'} · session ${room.id}`}
          >
            <Descriptions
              column={1}
              items={[
                { key: 'date', label: 'Historical date', children: room.date },
                {
                  key: 'duration',
                  label: 'Trigger to confirmation',
                  children: duration(room.side as 'legacy' | 'fast'),
                },
              ]}
            />
            <Link to={room.url} target="_blank" rel="noopener noreferrer">
              Open {room.side} session
            </Link>
          </Card>
        ))}
      </div>
      <Typography.Text type="secondary">
        Timings include dispatch, queueing and required financial work. Each
        timer freezes on its own server confirmation. Place matching trades
        independently in the two sessions.
      </Typography.Text>
      {step?.pending_legacy_work.length ? (
        <Alert
          type="info"
          message="Waiting for legacy work"
          description={step.pending_legacy_work.map((work, i) => (
            <div key={i}>
              {work.kind} · {work.status}
              {work.error && ` · ${work.error}`}
            </div>
          ))}
        />
      ) : null}
      <Table<TandemStep>
        size="small"
        rowKey="id"
        dataSource={pair.steps}
        pagination={false}
        columns={[
          { title: 'Period', dataIndex: 'ordinal' },
          { title: 'From', dataIndex: 'outgoing_date' },
          { title: 'To', dataIndex: 'target_date' },
          { title: 'Trigger', dataIndex: 'source' },
          { title: 'Legacy', render: (_, row) => seconds(row.legacy_seconds) },
          { title: 'Fast', render: (_, row) => seconds(row.fast_seconds) },
          {
            title: 'State',
            render: (_, row) =>
              `${row.both_confirmed_at ? 'Both confirmed' : `${row.legacy_status} / ${row.fast_status}`}${row.recovered ? ' · recovered fast work' : ''}`,
          },
        ]}
        expandable={{
          rowExpandable: row =>
            !!(row.diagnostics.length || row.legacy_error || row.fast_error),
          expandedRowRender: row => (
            <div>
              {row.legacy_error || row.fast_error}
              {row.diagnostics.map(item => (
                <pre key={item.id} style={{ whiteSpace: 'pre-wrap' }}>
                  {item.text}
                </pre>
              ))}
            </div>
          ),
        }}
      />
    </Space>
  );
}
